import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

const View = ({ banners, setOption, setSelectedBanner }) => {
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  // Primeiro, separar os banners CAROUSEL
  const carouselBanners = banners.filter(
    (banner) => banner.type === "CAROUSEL"
  );

  // Depois, separar e ordenar os banners STANDARD por position
  const standardBanners = banners
    .filter((banner) => banner.type === "STANDARD")
    .sort((a, b) => {
      const order = ["TOP", "MID", "BOTTOM"]; // Define a ordem desejada
      return order.indexOf(a.position) - order.indexOf(b.position);
    });

  // Combina os banners CAROUSEL no início e os STANDARD ordenados após
  const orderedBanners = [...carouselBanners, ...standardBanners];

  return (
    <Box sx={{ px: { xs: 0, md: 2 }, py: 4 }}>
      <Box display="flex" justifyContent="flex-end" mb={5}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOption("create")}
        >
          <AddIcon />
          Criar Novo Banner
        </Button>
      </Box>

      <Grid container spacing={5}>
        {orderedBanners.map((banner) => (
          <Grid item xs={12} key={banner.id}>
            <Banner
              banner={banner}
              setSelectedBannerId={setSelectedBannerId}
              setSelectedBanner={setSelectedBanner}
              isSelected={selectedBannerId === banner.id}
              setOption={setOption}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default View;

const Banner = ({
  banner,
  setSelectedBannerId,
  isSelected,
  setSelectedBanner,
  setOption,
}) => {
  const {
    id,
    type,
    title,
    text,
    href,
    imgUrl,
    imgUrlMobile,
    videoUrl,
    carousel,
    position,
  } = banner;
  const b = banner;

  // Função para extrair o ID do Vimeo da URL ou retornar o próprio valor se já for um ID
  const getVimeoVideoId = (url) => {
    if (!url) return null;

    // Se for apenas um número, assume que é o ID do Vimeo
    if (/^\d+$/.test(url.trim())) {
      return url.trim();
    }

    // Tenta extrair o ID de diferentes formatos de URL do Vimeo
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  };

  // Standard Banner
  const renderStandardBanner = () => (
    <Card elevation={isSelected ? 6 : 2}>
      <CardContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" justifyContent="space-between" gap={1}>
            <Typography variant="h6">{title}</Typography>
            <button
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2"
              onClick={() => {
                setSelectedBannerId(id);
                setSelectedBanner(b);
                setOption("edit");
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
              Editar Card
            </button>
          </Box>

          <Divider />
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2">
              <strong>Posição:</strong>{" "}
              {position === "TOP"
                ? "Topo"
                : position === "MID"
                ? "Meio"
                : "Rodapé"}
            </Typography>
            <Typography variant="body2">
              <strong>Link:</strong> {href}
            </Typography>
            <Typography variant="body2">
              <strong>Descrição:</strong> {text}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <div className="flex justify-center m-2 flex-col md:flex-row gap-3 p-5">
        <div className="w-full md:w-1/2">
          {(() => {
            const vimeoId = getVimeoVideoId(imgUrl || videoUrl);
            if (vimeoId) {
              return (
                <>
                  <p className="text-sm bg-purple-500 text-white mb-2 text-center p-3 rounded-lg">
                    Vídeo Vimeo
                  </p>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: 640,
                      paddingBottom: "56.25%", // 16:9 aspect ratio
                      height: 0,
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: 2,
                      border: "1px solid #ccc",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <Box
                      component="iframe"
                      src={`https://player.vimeo.com/video/${vimeoId}`}
                      title={`Vídeo Vimeo - ${title}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                </>
              );
            }
            return (
              <>
                <p className="text-sm bg-red-500 text-white mb-2 text-center p-3 rounded-lg">
                  Imagem Desktop
                </p>
                <CardMedia
                  component="img"
                  image={imgUrl}
                  alt={title}
                  sx={{
                    width: { xs: "100%", sm: 300, md: 400 },
                    height: "auto",
                    objectFit: "cover",
                    mx: "auto",
                    p: 2,
                    borderRadius: 2,
                  }}
                />
              </>
            );
          })()}
        </div>
        {imgUrlMobile && (
          <div className="w-full md:w-1/2">
            <p className="text-sm bg-blue-500 text-white mb-2 text-center p-3 rounded-lg">
              Imagem Mobile
            </p>
            <CardMedia
              component="img"
              image={imgUrlMobile}
              alt={title}
              sx={{
                width: "100%",
                height: 250,
                objectFit: "contain",
                mt: 2,
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );

  // Carousel Banner
  const renderCarouselBanner = () => (
    <Card elevation={isSelected ? 6 : 2}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Typography variant="h6">{title}</Typography>
          <button
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2"
            onClick={() => {
              setSelectedBannerId(id);
              setSelectedBanner(b);
              setOption("edit");
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
            Editar Card
          </button>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Tipo:</strong> Carrossel
        </Typography>
        <Typography variant="body2">
          <strong>Descrição:</strong> {text}
        </Typography>
      </CardContent>

      <Grid container spacing={2} justifyContent="left" sx={{ px: 2, pb: 3 }}>
        {carousel.map((item, index) => {
          const carouselVimeoId = getVimeoVideoId(item.imgUrl);
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={1}>
                {carouselVimeoId ? (
                  <>
                    <p className="text-sm bg-purple-500 text-white mb-2 text-center p-3 rounded-lg">
                      Vídeo Vimeo
                    </p>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        paddingBottom: "56.25%", // 16:9 aspect ratio
                        height: 0,
                        borderRadius: 1,
                        overflow: "hidden",
                        boxShadow: 1,
                        border: "1px solid #ccc",
                        mt: 3,
                        mb: 6,
                      }}
                    >
                      <Box
                        component="iframe"
                        src={`https://player.vimeo.com/video/${carouselVimeoId}`}
                        title={`Vídeo Vimeo Carousel ${index + 1}`}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <p className="text-sm bg-red-500 text-white mb-2 text-center p-3 rounded-lg">
                      Imagem Desktop
                    </p>
                    <CardMedia
                      component="img"
                      image={item.imgUrl}
                      alt={`Carousel ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "contain",
                      }}
                    />
                  </>
                )}
                {item.imgUrlMobile && (
                  <>
                    <p className="text-sm bg-blue-500 text-white mb-2 text-center p-3 rounded-lg">
                      Imagem Mobile
                    </p>
                    <CardMedia
                      component="img"
                      image={item.imgUrlMobile}
                      alt={`Carousel ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "contain",
                        mt: 2,
                      }}
                    />
                  </>
                )}
                <CardContent sx={{ mt: 2, backgroundColor: "#f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Link:</strong> {item.href}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2, mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedBannerId(id);
            setSelectedBanner(b);
            setOption("edit");
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
          Editar Card
        </Button>
      </CardActions> */}
    </Card>
  );

  // const handleBannerClick = () => {
  //   setSelectedBannerId(id); // Atualiza o estado do banner selecionado no componente pai

  //   setSelectedBanner(b);
  // };
  return (
    <Box className="w-full mx-auto" sx={{ borderRadius: 3 }}>
      {type === "STANDARD" ? renderStandardBanner() : renderCarouselBanner()}
    </Box>
  );
};
